import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, Clock, Mail, Calendar, Save, Loader2, Link2, Unlink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_BUSINESS_HOURS = {
  Monday: { is_open: true, open_time: '09:30', close_time: '16:30' },
  Tuesday: { is_open: true, open_time: '09:30', close_time: '16:30' },
  Wednesday: { is_open: true, open_time: '09:00', close_time: '15:00' },
  Thursday: { is_open: true, open_time: '09:30', close_time: '16:30' },
  Friday: { is_open: true, open_time: '09:00', close_time: '15:00' },
  Saturday: { is_open: false, open_time: '09:00', close_time: '17:00' },
  Sunday: { is_open: false, open_time: '09:00', close_time: '17:00' }
};

// Helper to convert 24hr to 12hr format
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Helper to convert 12hr to 24hr format
const parseTime12hr = (hours, minutes, period) => {
  let hrs = parseInt(hours);
  if (period === 'PM' && hrs !== 12) hrs += 12;
  if (period === 'AM' && hrs === 12) hrs = 0;
  return `${hrs.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Custom time picker component
const TimePicker = ({ value, onChange }) => {
  const [hours, minutes] = (value || '09:00').split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  const handleChange = (newHours, newMinutes, newPeriod) => {
    const time24 = parseTime12hr(newHours, newMinutes, newPeriod);
    onChange(time24);
  };
  
  return (
    <div className="flex items-center gap-1">
      <select 
        value={displayHours}
        onChange={(e) => handleChange(e.target.value, minutes, period)}
        className="h-9 rounded-md border border-input bg-background px-2 py-1 text-sm"
      >
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span>:</span>
      <select
        value={minutes}
        onChange={(e) => handleChange(displayHours, e.target.value, period)}
        className="h-9 rounded-md border border-input bg-background px-2 py-1 text-sm"
      >
        {[0, 15, 30, 45].map(m => (
          <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={(e) => handleChange(displayHours, minutes, e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 py-1 text-sm"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

export default function AdminAppointmentSettings() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: '' });
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [settings, setSettings] = useState({
    reminder_1_hours: 24,
    reminder_2_hours: 2,
    send_admin_reminders: true,
    send_client_reminders: true,
    confirmation_required: true,
    confirmation_expiry_hours: 48,
    auto_add_to_calendar: true,
    business_hours: DEFAULT_BUSINESS_HOURS,
    confirmation_email_subject: 'Please Confirm Your Appointment',
    reminder_email_subject: 'Appointment Reminder',
    admin_email: 'dentureking@gmail.com',
  });

  const fetchSettings = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/appointment-settings');
      // Merge with defaults to ensure all days exist
      const mergedHours = { ...DEFAULT_BUSINESS_HOURS, ...(data.business_hours || {}) };
      setSettings(prev => ({ ...prev, ...data, business_hours: mergedHours }));
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const status = await apiRequest('/oauth/calendar/status');
      setGoogleStatus(status);
    } catch (error) {
      console.log('Google Calendar status check failed');
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchGoogleStatus();
    
    // Check for URL params from OAuth callback
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      fetchGoogleStatus();
    } else if (searchParams.get('error') === 'google_auth_failed') {
      toast.error('Failed to connect Google Calendar. Please try again.');
    }
  }, [searchParams, fetchSettings, fetchGoogleStatus]);

  const connectGoogleCalendar = async () => {
    setConnectingGoogle(true);
    try {
      const data = await apiRequest('/oauth/calendar/login');
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to initiate Google Calendar connection');
      setConnectingGoogle(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await apiRequest('/oauth/calendar/disconnect', { method: 'POST' });
      setGoogleStatus({ connected: false, email: '' });
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiRequest('/admin/appointment-settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDayHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-appointment-settings">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Appointment Settings
          </h1>
          <p className="text-slate-500 mt-1">Configure appointment scheduling and reminders</p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Reminder Settings */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-orange-600" />
              Reminder Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="reminder_1">First Reminder (hours before)</Label>
                <Input
                  id="reminder_1"
                  type="number"
                  min="1"
                  value={settings.reminder_1_hours}
                  onChange={(e) => setSettings(prev => ({ ...prev, reminder_1_hours: parseInt(e.target.value) || 24 }))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Default: 24 hours (1 day before)</p>
              </div>
              <div>
                <Label htmlFor="reminder_2">Second Reminder (hours before)</Label>
                <Input
                  id="reminder_2"
                  type="number"
                  min="1"
                  value={settings.reminder_2_hours}
                  onChange={(e) => setSettings(prev => ({ ...prev, reminder_2_hours: parseInt(e.target.value) || 2 }))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Default: 2 hours before</p>
              </div>
            </div>
            
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Send Reminders to Clients</Label>
                  <p className="text-xs text-slate-500">Clients will receive reminder emails</p>
                </div>
                <Switch
                  checked={settings.send_client_reminders}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, send_client_reminders: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Send Reminders to Admin</Label>
                  <p className="text-xs text-slate-500">You will receive reminder emails</p>
                </div>
                <Switch
                  checked={settings.send_admin_reminders}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, send_admin_reminders: checked }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Settings */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-orange-600" />
              Confirmation Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Require Client Confirmation</Label>
                <p className="text-xs text-slate-500">Clients must confirm appointments via email link</p>
              </div>
              <Switch
                checked={settings.confirmation_required}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, confirmation_required: checked }))}
              />
            </div>
            
            <div>
              <Label htmlFor="expiry">Confirmation Link Expiry (hours)</Label>
              <Input
                id="expiry"
                type="number"
                min="1"
                value={settings.confirmation_expiry_hours}
                onChange={(e) => setSettings(prev => ({ ...prev, confirmation_expiry_hours: parseInt(e.target.value) || 48 }))}
                className="mt-1 max-w-xs"
              />
              <p className="text-xs text-slate-500 mt-1">How long clients have to confirm their appointment</p>
            </div>
            
            <div>
              <Label htmlFor="conf_subject">Confirmation Email Subject</Label>
              <Input
                id="conf_subject"
                value={settings.confirmation_email_subject}
                onChange={(e) => setSettings(prev => ({ ...prev, confirmation_email_subject: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="rem_subject">Reminder Email Subject</Label>
              <Input
                id="rem_subject"
                value={settings.reminder_email_subject}
                onChange={(e) => setSettings(prev => ({ ...prev, reminder_email_subject: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="admin_email">Admin Notification Email</Label>
              <Input
                id="admin_email"
                type="email"
                value={settings.admin_email}
                onChange={(e) => setSettings(prev => ({ ...prev, admin_email: e.target.value }))}
                className="mt-1"
                placeholder="dentureking@gmail.com"
              />
              <p className="text-xs text-slate-500 mt-1">Email address to receive appointment notifications and reminders</p>
            </div>
          </CardContent>
        </Card>

        {/* Business Hours */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-orange-600" />
              Business Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">Set different hours for each day of the week</p>
            <div className="space-y-3">
              {DAYS_OF_WEEK.map((day) => {
                const dayHours = settings.business_hours?.[day] || DEFAULT_BUSINESS_HOURS[day];
                return (
                  <div 
                    key={day} 
                    className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                      dayHours.is_open ? 'bg-slate-50' : 'bg-slate-100'
                    }`}
                  >
                    {/* Day name and toggle */}
                    <div className="w-28 flex items-center gap-2">
                      <Switch
                        checked={dayHours.is_open}
                        onCheckedChange={(checked) => updateDayHours(day, 'is_open', checked)}
                      />
                      <span className={`font-medium ${dayHours.is_open ? 'text-slate-900' : 'text-slate-400'}`}>
                        {day.slice(0, 3)}
                      </span>
                    </div>
                    
                    {/* Hours inputs */}
                    {dayHours.is_open ? (
                      <div className="flex items-center gap-2 flex-1">
                        <TimePicker
                          value={dayHours.open_time}
                          onChange={(time) => updateDayHours(day, 'open_time', time)}
                        />
                        <span className="text-slate-500">to</span>
                        <TimePicker
                          value={dayHours.close_time}
                          onChange={(time) => updateDayHours(day, 'close_time', time)}
                        />
                      </div>
                    ) : (
                      <span className="text-slate-400 italic">Closed</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Calendar Integration */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="w-5 h-5 text-orange-600" />
              Calendar Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Add to Calendar</Label>
                <p className="text-xs text-slate-500">Include "Add to Calendar" links in confirmation emails</p>
              </div>
              <Switch
                checked={settings.auto_add_to_calendar}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_add_to_calendar: checked }))}
              />
            </div>
            
            {/* Google Calendar Connection */}
            <div className="border-t pt-6">
              <Label className="mb-3 block">Google Calendar Sync</Label>
              
              {googleStatus.connected ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Connected to Google Calendar
                      </p>
                      <p className="text-sm text-green-700 mt-1">{googleStatus.email}</p>
                      <p className="text-xs text-green-600 mt-2">
                        Confirmed appointments will be automatically added to your Google Calendar.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={disconnectGoogleCalendar}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Unlink className="w-4 h-4 mr-1" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-sm text-slate-700 mb-3">
                    Connect your Google Calendar to automatically sync confirmed appointments.
                  </p>
                  <Button
                    onClick={connectGoogleCalendar}
                    disabled={connectingGoogle}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {connectingGoogle ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Connect Google Calendar
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
